import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { timeLookupsAPI, timeProfilesAPI, timeReportsAPI } from '../services/api';
import { TimeCustomerLookup, TimeEntry, TimeProfile, TimeProjectLookup, TimeReportFilter, TimeSummaryReport } from '../types';
import { downloadBlob, endOfMonthIso, endOfWeekIso, formatDate, formatHours, startOfMonthIso, startOfWeekIso, todayIso } from '../utils';
import DateInput from './DateInput';
import TableLoadingRow from './TableLoadingRow';
import TimeSelect, { TimeSelectOption } from './TimeSelect';
import ReportEntryEditDialog from './ReportEntryEditDialog';

const SummaryLoadingState = ({ label }: { label: string }) => (
  <div className="table-loading-state" role="status" aria-live="polite" aria-label={label}>
    <span className="spinner-border spinner-border-sm" aria-hidden="true" />
    <span>{label}</span>
  </div>
);

const emptySummary: TimeSummaryReport = {
  startDate: todayIso(),
  endDate: todayIso(),
  entryCount: 0,
  profileCount: 0,
  totalMinutes: 0,
  totalHours: 0,
  billableMinutes: 0,
  billableHours: 0,
  nonBillableMinutes: 0,
  nonBillableHours: 0,
  byProfile: [],
  byProject: [],
  byCustomer: [],
  byBillableStatus: [],
};

type ReportGroupKey = 'none' | 'customer' | 'project' | 'profile';
type ActiveReportGroupKey = Exclude<ReportGroupKey, 'none'>;

interface ReportGroupNode {
  id: string;
  key: ActiveReportGroupKey;
  label: string;
  totalHours: number;
  entryCount: number;
  children: ReportGroupNode[];
}

const reportGroupOptions: Array<{ value: ReportGroupKey; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'customer', label: 'Client' },
  { value: 'project', label: 'Project' },
  { value: 'profile', label: 'User' },
];

const REPORTS_FILTERS_STORAGE_KEY = 'timeReports.filters';
const REPORT_GROUPING_TAKE = 500;
const REPORT_EXPORT_BASE_FILENAME = 'TimeEntries';
const REPORT_EXPORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isLeapYear = (year: number) => year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

const getDaysInMonth = (year: number, month: number) => {
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }

  return [4, 6, 9, 11].includes(month) ? 30 : 31;
};

const formatReportExportDate = (value?: string | null) => {
  const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  const [, yearText, monthText, dayText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);

  if (month < 1 || month > 12 || day < 1 || day > getDaysInMonth(year, month)) {
    return null;
  }

  return `${dayText}_${REPORT_EXPORT_MONTHS[month - 1]}_${yearText.slice(-2)}`;
};

const buildReportExportFilename = (filters: TimeReportFilter, extension: 'xlsx' | 'pdf') => {
  const startDate = formatReportExportDate(filters.startDate);
  const endDate = formatReportExportDate(filters.endDate);
  const suffix = startDate && endDate ? `_${startDate}_${endDate}` : '';

  return `${REPORT_EXPORT_BASE_FILENAME}${suffix}.${extension}`;
};

const getDefaultReportFilters = (): TimeReportFilter => ({
  startDate: startOfWeekIso(),
  endDate: endOfWeekIso(),
  skip: 0,
  take: 50,
});

const getStoredReportFilters = (canViewAllReports: boolean): TimeReportFilter => {
  try {
    const storedFilters = localStorage.getItem(REPORTS_FILTERS_STORAGE_KEY);
    if (!storedFilters) {
      return getDefaultReportFilters();
    }

    const parsedFilters = JSON.parse(storedFilters) as TimeReportFilter;
    return {
      startDate: parsedFilters.startDate || startOfWeekIso(),
      endDate: parsedFilters.endDate || endOfWeekIso(),
      skip: parsedFilters.skip || 0,
      take: parsedFilters.take || 50,
      timeProfileId: canViewAllReports ? parsedFilters.timeProfileId || undefined : undefined,
      customerId: parsedFilters.customerId || undefined,
      customerIds: parsedFilters.customerIds && parsedFilters.customerIds.length > 0 ? parsedFilters.customerIds : undefined,
      projectId: parsedFilters.projectId || undefined,
      projectIds: parsedFilters.projectIds && parsedFilters.projectIds.length > 0 ? parsedFilters.projectIds : undefined,
      isBillable: parsedFilters.isBillable,
      search: parsedFilters.search || undefined,
    };
  } catch {
    return getDefaultReportFilters();
  }
};

const storeReportFilters = (filters: TimeReportFilter) => {
  try {
    localStorage.setItem(REPORTS_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors; filters still work for the current session.
  }
};

const sanitizeReportFilters = (filters: TimeReportFilter, canViewAllReports: boolean): TimeReportFilter => ({
  ...filters,
  timeProfileId: canViewAllReports ? filters.timeProfileId : undefined,
});

const billingFilterOptions: TimeSelectOption<string>[] = [
  { value: 'all', label: 'All billing' },
  { value: 'true', label: 'Billable' },
  { value: 'false', label: 'Non-billable' },
];

const getEntryHours = (entry: TimeEntry) => entry.hours ?? entry.durationMinutes / 60;

const getGroupValue = (entry: TimeEntry, groupKey: ActiveReportGroupKey) => {
  if (groupKey === 'customer') {
    return {
      id: String(entry.customerId ?? 'unassigned'),
      label: entry.customerTitle || 'Unassigned / internal',
    };
  }

  if (groupKey === 'project') {
    return {
      id: String(entry.projectId ?? 'unassigned'),
      label: entry.projectTitle || 'Unassigned / internal',
    };
  }

  return {
    id: String(entry.timeProfileId),
    label: entry.timeProfileName || 'Unknown user',
  };
};

const buildReportGroups = (entries: TimeEntry[], groupLevels: ActiveReportGroupKey[]): ReportGroupNode[] => {
  if (groupLevels.length === 0) {
    return [];
  }

  const [currentLevel, ...childLevels] = groupLevels;
  const groups = new Map<string, { id: string; label: string; entries: TimeEntry[] }>();

  entries.forEach(entry => {
    const groupValue = getGroupValue(entry, currentLevel);
    const mapKey = `${groupValue.id}:${groupValue.label}`;
    if (!groups.has(mapKey)) {
      groups.set(mapKey, { ...groupValue, entries: [] });
    }
    groups.get(mapKey)!.entries.push(entry);
  });

  return Array.from(groups.values())
    .map(group => ({
      id: group.id,
      key: currentLevel,
      label: group.label,
      totalHours: group.entries.reduce((sum, entry) => sum + getEntryHours(entry), 0),
      entryCount: group.entries.length,
      children: buildReportGroups(group.entries, childLevels),
    }))
    .sort((a, b) => b.totalHours - a.totalHours || a.label.localeCompare(b.label));
};

const ReportsPage: React.FC = () => {
  const { canViewAllReports, isAdmin } = useAuth();
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [projects, setProjects] = useState<TimeProjectLookup[]>([]);
  const [customers, setCustomers] = useState<TimeCustomerLookup[]>([]);
  const [summary, setSummary] = useState<TimeSummaryReport>(emptySummary);
  const [details, setDetails] = useState<TimeEntry[]>([]);
  const [groupingDetails, setGroupingDetails] = useState<TimeEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState<TimeReportFilter>(() => getStoredReportFilters(canViewAllReports));
  const [draftFilters, setDraftFilters] = useState<TimeReportFilter>(() => getStoredReportFilters(canViewAllReports));
  const [groupLevels, setGroupLevels] = useState<ReportGroupKey[]>(['none', 'none', 'none']);

  const page = useMemo(() => Math.floor((appliedFilters.skip || 0) / (appliedFilters.take || 50)) + 1, [appliedFilters.skip, appliedFilters.take]);
  const totalPages = Math.max(1, Math.ceil(totalCount / (appliedFilters.take || 50)));
  const selectedCustomerIds = useMemo(
    () => draftFilters.customerIds || (draftFilters.customerId ? [draftFilters.customerId] : []),
    [draftFilters.customerId, draftFilters.customerIds]
  );
  const selectedProjectIds = useMemo(
    () => draftFilters.projectIds || (draftFilters.projectId ? [draftFilters.projectId] : []),
    [draftFilters.projectId, draftFilters.projectIds]
  );
  const filteredProjects = useMemo(() => {
    if (selectedCustomerIds.length === 0) {
      return projects;
    }

    return projects.filter(project => selectedCustomerIds.includes(project.customerId));
  }, [projects, selectedCustomerIds]);
  const activeGroupLevels = useMemo(
    () => groupLevels.filter((level): level is ActiveReportGroupKey => level !== 'none'),
    [groupLevels]
  );
  const groupedDetails = useMemo(
    () => buildReportGroups(groupingDetails, activeGroupLevels),
    [activeGroupLevels, groupingDetails]
  );
  const isGroupedSummaryLoading = loading && activeGroupLevels.length > 0;
  const detailColumnCount = (canViewAllReports ? 8 : 6) + (isAdmin ? 1 : 0);

  useEffect(() => {
    if (!isAdmin) setEditingEntry(null);
  }, [isAdmin]);

  const loadLookups = async () => {
    const [profileData, projectData, customerData] = await Promise.all([
      timeProfilesAPI.getProfiles(false),
      timeLookupsAPI.getProjects(),
      timeLookupsAPI.getCustomers(),
    ]);
    setProfiles(profileData);
    setProjects(projectData);
    setCustomers(customerData);
  };

  const loadGroupingDetails = async (reportFilters: TimeReportFilter) => {
    const allItems: TimeEntry[] = [];
    let skip = 0;
    let total = 0;

    do {
      const page = await timeReportsAPI.getDetailed({
        ...reportFilters,
        skip,
        take: REPORT_GROUPING_TAKE,
      });
      allItems.push(...page.items);
      total = page.totalCount;
      skip += page.take || REPORT_GROUPING_TAKE;

      if (page.items.length === 0) {
        break;
      }
    } while (allItems.length < total);

    return allItems;
  };

  const loadReports = async (reportFilters: TimeReportFilter) => {
    setLoading(true);
    try {
      const [summaryData, detailedData, groupingData] = await Promise.all([
        timeReportsAPI.getSummary(reportFilters),
        timeReportsAPI.getDetailed(reportFilters),
        loadGroupingDetails(reportFilters),
      ]);
      setSummary(summaryData);
      setDetails(detailedData.items);
      setGroupingDetails(groupingData);
      setTotalCount(detailedData.totalCount);
    } catch {
      toast.error('Reports could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups().catch(() => toast.error('Lookups could not be loaded'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAllReports]);

  useEffect(() => {
    if (!canViewAllReports && (appliedFilters.timeProfileId || draftFilters.timeProfileId)) {
      setAppliedFilters(prev => ({ ...prev, timeProfileId: undefined, skip: 0 }));
      setDraftFilters(prev => ({ ...prev, timeProfileId: undefined, skip: 0 }));
    }
  }, [appliedFilters.timeProfileId, draftFilters.timeProfileId, canViewAllReports]);

  useEffect(() => {
    storeReportFilters(appliedFilters);
  }, [appliedFilters]);

  useEffect(() => {
    loadReports(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, canViewAllReports]);

  const updateDraftFilter = (patch: Partial<TimeReportFilter>) => {
    setDraftFilters(prev => ({ ...prev, ...patch, skip: patch.skip ?? 0 }));
  };

  const updateDraftCustomerFilter = (customerIds: number[]) => {
    setDraftFilters(prev => {
      const selectedProjectIds = prev.projectIds || (prev.projectId ? [prev.projectId] : []);
      const nextProjectIds = customerIds.length === 0
        ? selectedProjectIds
        : selectedProjectIds.filter(projectId => projects.some(project => project.id === projectId && customerIds.includes(project.customerId)));

      return {
        ...prev,
        customerId: undefined,
        customerIds: customerIds.length > 0 ? customerIds : undefined,
        projectId: undefined,
        projectIds: nextProjectIds.length > 0 ? nextProjectIds : undefined,
        skip: 0,
      };
    });
  };

  const updateDraftProjectFilter = (projectIds: number[]) => {
    updateDraftFilter({
      projectId: undefined,
      projectIds: projectIds.length > 0 ? projectIds : undefined,
    });
  };

  const applyFilters = () => {
    const nextFilters = sanitizeReportFilters({
      ...draftFilters,
      skip: 0,
      take: draftFilters.take || appliedFilters.take || 50,
    }, canViewAllReports);
    setDraftFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const clearFilters = () => {
    const defaultFilters = getDefaultReportFilters();
    setDraftFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  const applyDateRangeFilter = (startDate: string, endDate: string) => {
    updateDraftFilter({ startDate, endDate });
  };

  const applyThisWeekFilter = () => {
    const today = new Date();
    applyDateRangeFilter(startOfWeekIso(today), endOfWeekIso(today));
  };

  const applyLastWeekFilter = () => {
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    applyDateRangeFilter(startOfWeekIso(lastWeek), endOfWeekIso(lastWeek));
  };

  const applyThisMonthFilter = () => {
    const today = new Date();
    applyDateRangeFilter(startOfMonthIso(today), endOfMonthIso(today));
  };

  const applyLastMonthFilter = () => {
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    applyDateRangeFilter(startOfMonthIso(lastMonth), endOfMonthIso(lastMonth));
  };

  const exportReport = async () => {
    try {
      const blob = await timeReportsAPI.exportDetailed(appliedFilters);
      downloadBlob(blob, buildReportExportFilename(appliedFilters, 'xlsx'));
    } catch {
      toast.error('Excel export failed');
    }
  };

  const exportPdfReport = async () => {
    try {
      const blob = await timeReportsAPI.exportDetailedPdf(appliedFilters);
      downloadBlob(blob, buildReportExportFilename(appliedFilters, 'pdf'));
    } catch {
      toast.error('PDF export failed');
    }
  };

  const changePage = (direction: -1 | 1) => {
    const take = appliedFilters.take || 50;
    const nextPage = Math.min(totalPages, Math.max(1, page + direction));
    setAppliedFilters(prev => ({ ...prev, skip: (nextPage - 1) * take }));
  };

  const updateGroupLevel = (index: number, value: ReportGroupKey) => {
    setGroupLevels(prev => prev.map((level, levelIndex) => levelIndex === index ? value : level));
  };

  const renderGroupNodes = (nodes: ReportGroupNode[], depth = 0): React.ReactNode => {
    // The UI currently supports three grouping levels; deeper future levels
    // deterministically reuse the third visual style without changing data-depth.
    const visualDepth = Math.min(depth, 2);

    return (
      <div className={`report-group-list depth-${depth}`}>
        {nodes.map(node => (
          <div
            className={`report-group-node report-group-node-depth-${visualDepth}`}
            data-depth={depth}
            data-testid={`group-${node.key}-${node.label}`}
            key={`${node.key}-${node.id}-${node.label}`}
          >
            <div className="report-group-row">
              <div>
                <span className="report-group-label">{node.label}</span>
                <span className="report-group-meta">{node.entryCount} {node.entryCount === 1 ? 'entry' : 'entries'}</span>
              </div>
              <strong>{formatHours(node.totalHours)}</strong>
            </div>
            {node.children.length > 0 && renderGroupNodes(node.children, depth + 1)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fade-in">
      {isAdmin && editingEntry && (
        <ReportEntryEditDialog
          key={editingEntry.id}
          entry={editingEntry}
          profiles={profiles}
          projects={projects}
          onClose={() => setEditingEntry(null)}
          onSaved={() => {
            setEditingEntry(null);
            toast.success('Entry updated');
            loadReports(appliedFilters);
          }}
        />
      )}
      <div className="page-header">
        <div>
          <h1>Reports</h1>
          <p>{formatDate(appliedFilters.startDate || summary.startDate)} - {formatDate(appliedFilters.endDate || summary.endDate)}</p>
        </div>
        <div className="action-row">
          <button className="btn btn-success" type="button" onClick={exportReport} disabled={loading || totalCount === 0}>
            <i className="fa-solid fa-file-excel" aria-hidden="true" />
            Export Excel
          </button>
          <button className="btn btn-secondary" type="button" onClick={exportPdfReport} disabled={loading || totalCount === 0}>
            <i className="fa-solid fa-file-pdf" aria-hidden="true" />
            Export PDF
          </button>
        </div>
      </div>

      <section className="card">
        <div className="card-body">
          <div className="filter-bar reports-filter-bar" data-testid="reports-filter-bar">
            <DateInput
              className="form-control filter-date-control filter-start-date-control"
              aria-label="Start date filter"
              value={draftFilters.startDate || ''}
              onChange={(value) => updateDraftFilter({ startDate: value })}
            />
            <DateInput
              className="form-control filter-date-control filter-end-date-control"
              aria-label="End date filter"
              value={draftFilters.endDate || ''}
              onChange={(value) => updateDraftFilter({ endDate: value })}
            />
            <div className="quick-date-filter-row filter-quick-date-control" aria-label="Quick date filters">
              <button className="btn btn-secondary btn-sm" type="button" onClick={applyThisWeekFilter}>This week</button>
              <button className="btn btn-secondary btn-sm" type="button" onClick={applyLastWeekFilter}>Last week</button>
              <button className="btn btn-secondary btn-sm" type="button" onClick={applyThisMonthFilter}>This month</button>
              <button className="btn btn-secondary btn-sm" type="button" onClick={applyLastMonthFilter}>Last month</button>
            </div>
            {canViewAllReports && (
              <TimeSelect
                inputId="reports-employee-filter"
                className="filter-employee-control"
                ariaLabel="Member filter"
                options={[
                  { value: 0, label: 'All members' },
                  ...profiles.map(profile => ({ value: profile.id, label: profile.displayName })),
                ]}
                value={draftFilters.timeProfileId || 0}
                onChange={(value) => updateDraftFilter({ timeProfileId: Number(value || 0) || undefined })}
                placeholder="All members"
              />
            )}
            <TimeSelect<number>
              inputId="reports-customer-filter"
              className="filter-customer-control"
              ariaLabel="Customer filter"
              options={customers.map(customer => ({ value: customer.id, label: customer.title }))}
              value={selectedCustomerIds}
              onChange={(values) => updateDraftCustomerFilter(values)}
              placeholder="All customers"
              isClearable
              isMulti
            />
            <TimeSelect<number>
              inputId="reports-project-filter"
              className="filter-project-control"
              ariaLabel="Project filter"
              options={filteredProjects.map(project => ({ value: project.id, label: `${project.customerTitle} - ${project.title}` }))}
              value={selectedProjectIds}
              onChange={(values) => updateDraftProjectFilter(values)}
              placeholder="All projects"
              isClearable
              isMulti
            />
            <TimeSelect
              inputId="reports-billing-filter"
              className="filter-billing-control"
              ariaLabel="Billing filter"
              options={billingFilterOptions}
              value={draftFilters.isBillable === undefined ? 'all' : String(draftFilters.isBillable)}
              onChange={(value) => updateDraftFilter({
                isBillable: value === 'all' ? undefined : value === 'true',
              })}
              placeholder="All billing"
            />
            <input
              className="form-control filter-search-control"
              aria-label="Search reports"
              placeholder="Search"
              value={draftFilters.search || ''}
              onChange={(event) => updateDraftFilter({ search: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !loading) {
                  applyFilters();
                }
              }}
            />
            <button className="btn btn-primary filter-apply-control" type="button" onClick={applyFilters} disabled={loading}>
              Filter
            </button>
            <button className="btn btn-outline-secondary filter-clear-control" type="button" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        </div>
      </section>

      <div className="stat-grid">
        <div className="stat-card">
          <span>Total</span>
          <strong>{formatHours(summary.totalHours)}</strong>
        </div>
        <div className="stat-card">
          <span>Billable</span>
          <strong>{formatHours(summary.billableHours)}</strong>
        </div>
        <div className="stat-card">
          <span>Non-billable</span>
          <strong>{formatHours(summary.nonBillableHours)}</strong>
        </div>
        <div className="stat-card">
          <span>Entries</span>
          <strong>{summary.entryCount}</strong>
        </div>
      </div>

      <div className={`time-grid ${canViewAllReports ? 'three-col' : 'two-col'}`}>
        {canViewAllReports && (
          <section className="card">
            <div className="card-header"><h5 className="card-title">Members</h5></div>
            <div className="card-body">
              {loading ? (
                <SummaryLoadingState label="Loading members..." />
              ) : (
                <div className="summary-list">
                  {summary.byProfile.map(item => (
                    <div className="summary-row" key={item.id || item.key}>
                      <span>{item.key}</span>
                      <strong>{formatHours(item.totalHours)}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
        <section className="card" aria-label="Project budgets">
          <div className="card-header"><h5 className="card-title">Projects</h5></div>
          <div className="card-body">
            {loading ? (
              <SummaryLoadingState label="Loading projects..." />
            ) : (
              <div className="summary-list budget-list">
                {summary.byProject.map(item => {
                  const hasBudget = item.budgetHours !== undefined && item.budgetHours !== null && item.budgetHours > 0;
                  return (
                    <div className="summary-row budget-row" key={item.id || item.key}>
                      <div className="budget-row-main">
                        <span>{item.key}</span>
                        {hasBudget && (
                          <div className="budget-progress" aria-label={`${item.key} budget progress`}>
                            <div className="budget-progress-bar" style={{ width: `${Math.min(100, Math.max(0, item.budgetUsedPercent || 0))}%` }} />
                          </div>
                        )}
                      </div>
                      <div className="budget-row-values">
                        {hasBudget ? (
                          <>
                            <strong>{formatHours(item.totalHours)} / {formatHours(item.budgetHours)}</strong>
                            <span>{formatHours(item.remainingBudgetHours)} remaining</span>
                            <span>{Math.round(item.budgetUsedPercent || 0)}% used</span>
                          </>
                        ) : (
                          <>
                            <strong>{formatHours(item.totalHours)}</strong>
                            <span>No budget set</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
        <section className="card">
          <div className="card-header"><h5 className="card-title">Customers</h5></div>
          <div className="card-body">
            {loading ? (
              <SummaryLoadingState label="Loading customers..." />
            ) : (
              <div className="summary-list">
                {summary.byCustomer.map(item => (
                  <div className="summary-row" key={item.id || item.key}>
                    <span>{item.key}</span>
                    <strong>{formatHours(item.totalHours)}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <section className="card" aria-label="Grouped summary">
        <div className="card-header">
          <h5 className="card-title">Grouped summary</h5>
        </div>
        <div className="card-body">
          <div className="filter-bar grouping-filter-bar">
            {groupLevels.map((level, index) => (
              <label className="form-field" htmlFor={`report-group-level-${index + 1}`} key={`group-level-${index + 1}`}>
                <span>Group level {index + 1}</span>
                <TimeSelect
                  inputId={`report-group-level-${index + 1}`}
                  ariaLabel={`Group level ${index + 1}`}
                  options={reportGroupOptions}
                  value={level}
                  onChange={(value) => updateGroupLevel(index, (value || 'none') as ReportGroupKey)}
                  placeholder="None"
                />
              </label>
            ))}
          </div>

          {activeGroupLevels.length === 0 && (
            <div className="empty-state">Choose up to three grouping levels: client, project, or user.</div>
          )}
          {isGroupedSummaryLoading && (
            <div className="empty-state" role="status" aria-label="Loading grouped summary">
              Loading grouped summary...
            </div>
          )}
          {activeGroupLevels.length > 0 && groupedDetails.length === 0 && !loading && (
            <div className="empty-state">No grouped entries</div>
          )}
          {activeGroupLevels.length > 0 && groupedDetails.length > 0 && !loading && renderGroupNodes(groupedDetails)}
        </div>
      </section>

      <section className="card">
        <div className="card-header">
          <h5 className="card-title">Detailed</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Date</th>
                  {canViewAllReports && <th>Member</th>}
                  {canViewAllReports && <th>Created by</th>}
                  <th>Customer</th>
                  <th>Project</th>
                  <th>Hours</th>
                  <th>Billable</th>
                  <th>Description</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading && <TableLoadingRow colSpan={detailColumnCount} label="Loading report entries..." />}
                {!loading && details.map(entry => (
                  <tr key={entry.id}>
                    <td data-label="Date">{formatDate(entry.workDate)}</td>
                    {canViewAllReports && <td data-label="Member">{entry.timeProfileName}</td>}
                    {canViewAllReports && <td data-label="Created by">{entry.createdByUserEmail || '—'}</td>}
                    <td data-label="Customer">{entry.customerTitle || 'Unassigned / internal'}</td>
                    <td data-label="Project">{entry.projectTitle || 'Unassigned / internal'}</td>
                    <td data-label="Hours">{formatHours(entry.hours)}</td>
                    <td data-label="Billable">{entry.isBillable ? 'Yes' : 'No'}</td>
                    <td data-label="Description">{entry.description || ''}</td>
                    {isAdmin && (
                      <td data-label="Actions">
                        <button className="action-btn" type="button" onClick={() => setEditingEntry(entry)}
                          title="Edit" aria-label={`Edit entry for ${entry.timeProfileName}`}>
                          <i className="fa-solid fa-pen" aria-hidden="true" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {!loading && details.length === 0 && (
                  <tr>
                    <td colSpan={detailColumnCount}><div className="empty-state">No entries</div></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="pager-row">
              <span>Page {page} of {totalPages}</span>
              <div className="action-row">
                <button className="btn btn-secondary btn-sm" type="button" disabled={page <= 1} onClick={() => changePage(-1)}>
                  <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                </button>
                <button className="btn btn-secondary btn-sm" type="button" disabled={page >= totalPages} onClick={() => changePage(1)}>
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ReportsPage;
